import { Component, TemplateRef, ViewChild, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';

import { MessageService } from 'services/message/message.service';
import {
  AuthBiometricWebSocket,
  AuthService,
  ConfirmTransactionRequest,
  ConfirmTransactionResponse,
  defined,
  OtpType,
  TwoFactorData,
  TwoFactorOption,
  TwoFactorStatus,
  Utils,
} from 'core';
import { faMobileAlt, faQuestionCircle, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { Guid } from 'guid-typescript';

export type TwoFAModalResponse = {
  readonly modalData: any;
  readonly twoFA: TwoFactorData;
};

export type PushContext = {
  title?: string;
  summary?: string;
};

export type ModalConfig = {
  params?: unknown;
  otpType: OtpType;
  pushCtx?: PushContext;
  resendOpt?: () => void;
  closeCallback?: () => void;
  label?: string;
};

@Component({
  selector: 'app-2fa-modal',
  templateUrl: 'two-fa-modal.component.html',
  styleUrls: ['./two-fa-modal.scss'],
})
export class TwoFactorModalComponent implements OnDestroy {
  onlyNumericAllowed = Utils.onlyNumericAllowed;
  readonly faMobileAlt = faMobileAlt;
  readonly faQuestionCircle = faQuestionCircle;
  readonly faShieldAlt = faShieldAlt;

  private config = {
    animated: true,
    keyboard: true,
    backdrop: true,
    ignoreBackdropClick: false,
    class: 'otp-dialog-box',
  };
  @ViewChild('authenticationRef') authenticationRefRef: TemplateRef<unknown>;

  @Output() emitOtpToParent = new EventEmitter<TwoFAModalResponse>();
  private modalData: unknown;
  twoFactorStatus: TwoFactorStatus;
  otpCodeValue?: number;
  fallbackToGoogleAuth = false;
  private otpType: OtpType;
  private modalRef?: BsModalRef;
  label?: string;
  otpBeingProcessed = false;
  private closeCallback?: () => void;
  private resendOpt?: () => void;
  private hideSubscription: Subscription;
  private biometricWebsocket: AuthBiometricWebSocket<ConfirmTransactionResponse, ConfirmTransactionRequest> | null = null;
  private biometricWebsocketSub: Subscription | null = null;
  confirmationCode: string | null = null;

  constructor(
    private readonly modalService: BsModalService,
    private readonly messageService: MessageService,
    private readonly authService: AuthService
  ) {}

  ngOnDestroy(): void {
    this.disconnectBiometricWebSocket();
  }

  openModal(config: ModalConfig): Observable<TwoFAModalResponse> {
    this.otpType = config.otpType;
    this.otpCodeValue = undefined;
    this.modalData = config.params;
    this.otpBeingProcessed = false;
    this.label = config.label;
    this.fallbackToGoogleAuth = false;
    this.resendOpt = config.resendOpt;
    this.closeCallback = config.closeCallback;

    if (config.otpType == OtpType.FORGOT_PASSWORD) {
      this.twoFactorStatus = { options: [] }; // Setting no options triggers the email OTP option in the UI used for password reset.
      this.launchModal();
    } else {
      this.authService.getTwoFactorStatus().then(
        (status) => {
          this.twoFactorStatus = status;
          if (this.twoFactorStatus.options.length === 0) {
            this.sendOtpEmail(false);
          } else if (this.biometric2FAAvailable) {
            this.connectBiometricWebSocket(config.pushCtx);
          }
          this.launchModal();
        },
        (error) => this.messageService.showErrorMessage(error)
      );
    }

    // FIXME replace inflexible OTP passing via @Output with proper Observable
    return this.emitOtpToParent;
  }

  resendAppPush(): void {
    if (defined(this.biometricWebsocket)) {
      this.biometricWebsocket.resend();
    } else {
      console.error('Invalid state - resend with no socket connection');
    }
  }

  private connectBiometricWebSocket(pushCtx?: PushContext): void {
    const transactionId = Guid.create().toString();
    this.disconnectBiometricWebSocket();
    this.biometricWebsocket = new AuthBiometricWebSocket<ConfirmTransactionResponse, ConfirmTransactionRequest>();
    this.biometricWebsocketSub = this.biometricWebsocket.confirmResponse$.subscribe({
      next: (response) => {
        if (response.success && defined(response.confirmationCode)) {
          this.confirmationCode = response.confirmationCode;
        } else if (response.success && defined(response.data)) {
          this.emitOtpToParent.emit({ modalData: this.modalData, twoFA: { pushConfirmation: { token: response.data, transactionId } } });
        } else {
          this.messageService.showErrorMessage(response.errorMessage ?? '2FA failed.');
        }
      },
      error: (error) => this.messageService.error(error),
    });
    this.authService.getToken().then(
      (jwt) =>
        this.biometricWebsocket?.sendRequest({
          ConfirmTransactionRequest: {
            jwt,
            transactionId,
            title: this.getPushNotificationTitle(pushCtx),
            summary: pushCtx?.summary ?? '',
          },
        }),
      (error) => this.messageService.showErrorMessage(error)
    );
  }

  private getPushNotificationTitle(pushCtx?: PushContext): string {
    return pushCtx?.title ?? this.getDefaultPushNotificationTitle();
  }

  private getDefaultPushNotificationTitle(): string {
    switch (this.otpType) {
      case OtpType.WITHDRAW:
        return 'withdraw funds';
      case OtpType.TRADE:
        return 'place a trade';
      case OtpType.ZOOMME:
        return 'ZoomMe funds';
      case OtpType.EXCHANGE:
        return 'change your account settings';
      case OtpType.FORGOT_PASSWORD:
        return 'recover your password';
      case OtpType.CHANGE_PASSWORD:
        return 'change your password';
      case OtpType.SHOW_2FA_QR_CODE:
        return 'see your 2FA secret';
      case OtpType.RESET_2FA_SECRET:
        return 'reset your 2FA secret';
      case OtpType.CARD_MANAGEMENT:
        return 'manage your CoinZoom VISA card';
    }
  }

  private disconnectBiometricWebSocket(): void {
    this.biometricWebsocket?.disconnect();
    this.biometricWebsocketSub?.unsubscribe();
    this.biometricWebsocket = null;
    this.biometricWebsocketSub = null;
  }

  private launchModal(): void {
    this.modalRef = this.modalService.show(this.authenticationRefRef, this.config);
    const closeCallback = this.closeCallback;
    if (defined(closeCallback)) {
      this.hideSubscription = this.modalService.onHide.subscribe(() => {
        closeCallback();
        if (this.hideSubscription) {
          this.hideSubscription.unsubscribe();
        }
      });
    }

    setTimeout(() => document.getElementById('otpCode')?.focus(), 100);
  }

  get app2FAAvailable(): boolean {
    return (
      this.twoFactorStatus.options.includes(TwoFactorOption.CODE_2FA)
      && !this.twoFactorStatus.options.includes(TwoFactorOption.BIOMETRIC_2FA)
    );
  }

  get showBiometricOptions(): boolean {
    return this.biometric2FAAvailable && defined(this.confirmationCode);
  }

  get biometric2FAAvailable(): boolean {
    return this.twoFactorStatus.options.includes(TwoFactorOption.BIOMETRIC_2FA);
  }

  get email2FAAvailable(): boolean {
    return this.twoFactorStatus.options.length === 0;
  }

  sendOtpEmail(showMessage: boolean): void {
    if (this.resendOpt) {
      this.resendOpt();
    } else {
      this.authService.sentOtpToUser(this.otpType).subscribe({
        next: () => {
          if (showMessage) {
            this.messageService.success('The email has been sent');
          }
        },
        error: (error) => this.messageService.showErrorMessage(error),
      });
    }
  }

  hideModal(): void {
    if (this.hideSubscription) {
      this.hideSubscription.unsubscribe();
    }
    if (this.modalRef) {
      this.modalRef.hide();
      this.modalRef = undefined;
    }
    this.disconnectBiometricWebSocket();
    if (this.closeCallback) {
      this.closeCallback();
    }
  }

  otpProcessed(): void {
    this.otpBeingProcessed = false;
  }

  submitOtp(): void {
    if (!this.otpBeingProcessed && defined(this.otpCodeValue)) {
      this.otpBeingProcessed = true;
      this.emitOtpToParent.emit({ modalData: this.modalData, twoFA: { otp: this.otpCodeValue } });
    }
  }
}
